import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import type { InvoiceLineItemCover } from '@packages/types';
import { listInvoicesByLineItem } from '@packages/api/endpoints/invoice.js';
import { queryKeys } from '@packages/api';
import { chunkIds } from '../../lib/chunk-ids';

export interface UseInvoicesForLineItemsResult {
  /** One entry per requested line-item id that resolved to an invoice; a
   *  line-item id the caller has no access to, or that does not exist, is
   *  simply absent. */
  invoiceByLineItemId: Map<string, InvoiceLineItemCover>;
  isLoading: boolean;
  /** True on a failed read. Every caller here treats a failure the same as
   *  "not yet known" (an unresolved id is simply absent from the map), so
   *  none needs to branch on it directly - it is surfaced so a caller that
   *  ever does need to distinguish "still loading" from "failed" can. */
  isError: boolean;
}

/**
 * `GET .../invoices/by-line-item` is capped at 100 ids per call
 * (`InvoicesByLineItemQuerySchema`); its own JSDoc says a caller with more
 * ids must batch. This hook is the one place that batches, so no caller has
 * to reason about the cap itself. This happens to equal
 * `CLIENT_ID_RESOLUTION_BATCH_SIZE`, but it is not that constant reused: the
 * cap here comes from this one route's own schema, not the repo-wide
 * pagination ceiling that constant mirrors, so the two are free to diverge
 * if the route's cap ever changes independently.
 */
const MAX_IDS_PER_REQUEST = 100;

/**
 * Resolves which invoice each of the given line-item ids sits on, batched
 * into requests of at most 100 ids each (`GET .../invoices/by-line-item`).
 * Plain `useQuery` per batch, not an infinite list, so the send / mark-sent /
 * withdraw mutations invalidate the `queryKeys.invoicesByLineItemIds` prefix
 * directly - permitted per `.claude/rules/no-blanket-invalidate-infinite-query.md`,
 * which only bars blanket invalidation of a `useInfiniteQuery`, and a prefix
 * match still catches every batch's key.
 *
 * Feeds the activity "this work is on invoice INV-NNNN" alert/deeplink, the
 * draft editor's locked-line partition and move-conflict dialog, and the
 * omnibox's frozen-result marker - each caller reads `invoiceByLineItemId`
 * to learn whether a line item's invoice carries a live pro forma. A caller
 * that offers an action gated on this map (e.g. "Move here") must not act on
 * an id until `isLoading` is false and `isError` is false: an unresolved id
 * is indistinguishable from "not locked" in the map alone.
 *
 * Precedent: `storedEmailsByIds` (`components/activity/DiaryBody.tsx`).
 */
export function useInvoicesForLineItems(
  accountId: string | null | undefined,
  lineItemIds: readonly string[],
): UseInvoicesForLineItemsResult {
  // Deduped + sorted so the query key is stable for the same set regardless
  // of caller ordering (queryKeys.invoicesByLineItemIds documents this), then
  // split into batches the route will actually accept.
  const sortedIds = useMemo(
    () => [...new Set(lineItemIds)].sort(),
    [lineItemIds],
  );
  const batches = useMemo(() => chunkIds(sortedIds, MAX_IDS_PER_REQUEST), [sortedIds]);

  const queries = useQueries({
    queries: batches.map((batchIds) => ({
      queryKey: queryKeys.invoicesByLineItemIds(accountId ?? '', batchIds),
      queryFn: () => listInvoicesByLineItem(accountId!, batchIds),
      enabled: !!accountId && batchIds.length > 0,
    })),
  });

  const isLoading = sortedIds.length > 0 && queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);

  // `useQueries` returns a fresh array identity every render, so this map is
  // rebuilt each render rather than `useMemo`-cached against it - memoising
  // against the array itself would never hit, and memoising against each
  // query's `data` needs a dependency array whose length varies with the
  // batch count, which `react-hooks/exhaustive-deps` cannot verify. Rebuild
  // is O(total ids) and runs alongside a render that already recomputes the
  // partition this feeds, so the cost is negligible.
  const invoiceByLineItemId = new Map<string, InvoiceLineItemCover>();
  for (const query of queries) {
    const data = query.data;
    if (!data) continue;
    const invoicesById = new Map(data.invoices.map((invoice) => [invoice.id, invoice]));
    for (const entry of data.lineItems) {
      const invoice = invoicesById.get(entry.invoiceId);
      if (invoice) invoiceByLineItemId.set(entry.lineItemId, invoice);
    }
  }

  return { invoiceByLineItemId, isLoading, isError };
}
